<template>
  <div>
    <ul class="parcel-info">
      <li>
        <span class="d-block text-black-50 capitalize-first-letter">
          {{ t('from') }}
        </span>
        {{ from }}
      </li>
      <li>
        <span class="d-block text-black-50 capitalize-first-letter">
          {{ t('carrier') }}
        </span>
        <div class="d-flex carrier-info align-items-center">
          <img :alt="shipment.carrier" :src="carrierLogo" class="carrier-icon" />
          <span>{{ carrierName(shipment.carrier) }}</span>
        </div>
        <small v-if="forwardedBy" class="text-black-50">
          {{ t('forwardedBy', { carrier: carrierName(forwardedBy) }) }}
        </small>
      </li>
      <template v-if="showMore">
        <li>
          <span class="d-block text-black-50">
            {{ t('shipmentNumber') }}
          </span>
          {{ shipment.shipmentNumber }}
          <small v-if="originalShipmentNumber" class="text-black-50">
            ({{ t('previousShipmentNumber') }} {{ originalShipmentNumber }})
          </small>
        </li>
        <li>
          <span class="d-block text-black-50">
            {{ t('serviceType') }}
          </span>
          {{ capitalizeFirstLetter(itemData.service) }}
        </li>
        <li v-if="additionalServices">
          <span class="d-block text-black-50">
            {{ t('additionalServices') }}
          </span>
          {{ additionalServices }}
        </li>
      </template>
    </ul>

    <b-button variant="link" class="p-0 expand-list" :class="{ active: showMore }" @click="toggleShowMore">
      <span class="expand-list__label">{{ moreBtnText }}</span>
      <expand-icon />
    </b-button>

    <hr class="tracking-divider" />
  </div>
</template>

<script lang="ts">
import type { Shipment, TrackingData } from '@/types/Models/Shipment'
import { BButton } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalizeFirstLetter, carrierName } from '@/helpers/Utils'
import ExpandIcon from '@/views/Elements/Icons/ExpandIcon.vue'

const carrierLogos = import.meta.glob('../../assets/images/carriers/*-logo.svg', {
  eager: true,
  import: 'default',
  query: '?url',
}) as Record<string, string>

export default defineComponent({
  name: 'ShipmentInfo',
  components: { BButton, ExpandIcon },
  props: {
    originalShipment: {
      type: Object as PropType<Shipment | null>,
      required: false,
      default: null,
    },
    itemData: {
      type: Object as PropType<TrackingData>,
      required: true,
    },
    shipment: {
      type: Object as PropType<Shipment>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n()

    const showMore = ref(false)

    const moreBtnText = computed(() => (showMore.value ? t('viewLess') : t('viewMore')))
    const carrierLogo = computed(() => carrierLogos[`../../assets/images/carriers/${props.shipment.carrier}-logo.svg`])
    const forwardedBy = computed(() => props.originalShipment?.carrier)
    const originalShipmentNumber = computed(() => props.originalShipment?.shipmentNumber)
    const from = computed(() => props.itemData?.sender?.company || props.itemData?.sender?.name)

    const additionalServices = computed(() => {
      if (props.itemData.distributionChannel === 'package') {
        if (props.itemData.servicePoints.length > 0) {
          return t('servicePointDelivery')
        }
        return t('leaveAtTheDoor')
      }

      if (props.itemData.distributionChannel === 'parcel') {
        if (props.itemData.deliveryType === 'service_point') {
          return t('servicePointDelivery')
        }
        if (props.itemData.deliveryType === 'address') {
          return t('addressDelivery')
        }
      }

      return null
    })

    const toggleShowMore = () => {
      showMore.value = !showMore.value
    }

    return {
      from,
      carrierLogo,
      showMore,
      moreBtnText,
      forwardedBy,
      originalShipmentNumber,
      additionalServices,
      carrierName,
      toggleShowMore,
      capitalizeFirstLetter,
      t,
    }
  },
})
</script>
